import { useState } from 'react';
import { getSupabase } from '../../../lib/supabase';
import { VlogPost } from '../../../mocks/vlogPosts';

interface VlogGridProps {
  posts: VlogPost[];
  /** Total unfiltered post count — used to distinguish "no results" from "nothing uploaded" */
  totalPosts: number;
}

export default function VlogGrid({ posts, totalPosts }: VlogGridProps) {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [selectedVideo, setSelectedVideo] = useState<string | null>(null);

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setSubmitError('Please enter a valid email address');
      return;
    }
    setIsSubmitting(true);
    setSubmitError('');
    try {
      const { error } = await getSupabase()
        .from('vlog_subscribers')
        .insert([{ email: email.toLowerCase().trim(), source: 'vlog_page' }]);
      if (error) {
        if (error.code === '23505') {
          setSubmitError('This email is already subscribed!');
        } else {
          throw error;
        }
      } else {
        setSubmitSuccess(true);
        setEmail('');
        setTimeout(() => setSubmitSuccess(false), 5000);
      }
    } catch {
      setSubmitError('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div id="videos" className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Subscribe + TikTok strip */}
        <div className="mb-16 text-center">
          <div className="max-w-2xl mx-auto bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-8 border-2 border-amber-200">
            <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-orange-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-notification-3-line text-2xl text-white"></i>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-3">Never Miss an Episode</h3>
            <p className="text-gray-600 mb-6">
              Be the first to know when we drop new episodes of 2 Bald Tires
            </p>
            {submitSuccess ? (
              <div className="bg-green-50 border-2 border-green-200 rounded-xl p-6">
                <i className="ri-checkbox-circle-fill text-4xl text-green-600 mb-2 block"></i>
                <p className="text-green-800 font-semibold text-lg">You're on the list!</p>
                <p className="text-green-700 text-sm mt-1">We'll notify you when the next episode drops.</p>
              </div>
            ) : (
              <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  required
                  disabled={isSubmitting}
                  className="flex-1 px-4 py-3 rounded-lg border-2 border-gray-300 focus:border-amber-500 focus:ring-2 focus:ring-amber-200 outline-none text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                />
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-600 text-white font-semibold rounded-lg hover:from-amber-600 hover:to-orange-700 transition-all whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 cursor-pointer"
                >
                  {isSubmitting ? (
                    <><i className="ri-loader-4-line animate-spin"></i>Subscribing...</>
                  ) : (
                    <><i className="ri-mail-send-line"></i>Subscribe</>
                  )}
                </button>
              </form>
            )}
            {submitError && <p className="text-red-600 text-sm mt-3">{submitError}</p>}
          </div>

          {/* TikTok social strip */}
          <div className="mt-6 flex items-center justify-center gap-3 text-gray-600">
            <div className="h-px flex-1 bg-gradient-to-r from-transparent to-gray-300 max-w-24"></div>
            <a
              href="https://www.tiktok.com/@rockhoundauto"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-pink-50 to-purple-50 border border-pink-200 rounded-full hover:from-pink-100 hover:to-purple-100 transition-all group"
            >
              <i className="ri-tiktok-fill text-xl text-pink-600 group-hover:scale-110 transition-transform"></i>
              <span className="text-sm font-medium text-gray-700">
                Also on TikTok <span className="font-bold text-pink-600">@RockhoundAuto</span>
              </span>
              <span className="text-xs text-gray-500">— 45-second clips</span>
            </a>
            <div className="h-px flex-1 bg-gradient-to-l from-transparent to-gray-300 max-w-24"></div>
          </div>
        </div>

        {/* Grid / empty states */}
        {totalPosts === 0 ? (
          /* Nothing uploaded yet */
          <div className="text-center py-20">
            <div className="w-24 h-24 bg-gradient-to-br from-amber-100 to-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-film-line text-4xl text-amber-600"></i>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-3">First Episode Coming Soon</h3>
            <p className="text-gray-600 max-w-md mx-auto">
              The 2 Bald Tires are gearing up for their debut. Subscribe above to be notified when we launch!
            </p>
          </div>
        ) : posts.length === 0 ? (
          /* Filter returned no results */
          <div className="text-center py-20">
            <i className="ri-video-line text-6xl text-gray-300 mb-4 block"></i>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">No episodes found</h3>
            <p className="text-gray-600">Try adjusting your search or category filter</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {posts.map((post) => (
              <div
                key={post.id}
                className={`group bg-white rounded-xl overflow-hidden border-2 transition-all duration-300 h-full flex flex-col ${
                  post.featured
                    ? 'lg:col-span-2 border-amber-300 hover:border-amber-500'
                    : 'border-gray-200 hover:border-amber-400'
                } hover:shadow-xl`}
              >
                {/* Thumbnail */}
                <div
                  className={`relative bg-gradient-to-br from-gray-100 to-gray-200 overflow-hidden flex-shrink-0 ${
                    post.featured
                      ? 'aspect-video lg:aspect-auto lg:min-h-[260px]'
                      : 'aspect-video'
                  }`}
                >
                  <img
                    src={post.thumbnail}
                    alt={post.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />

                  {/* Play / Coming Soon overlay */}
                  {post.youtubeId ? (
                    <button
                      onClick={() => setSelectedVideo(post.youtubeId!)}
                      className="absolute inset-0 bg-black/40 group-hover:bg-black/50 transition-all flex items-center justify-center cursor-pointer"
                    >
                      <div
                        className={`bg-white/90 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform ${
                          post.featured ? 'w-20 h-20' : 'w-16 h-16'
                        }`}
                      >
                        <i
                          className={`ri-play-fill text-amber-600 ml-1 ${post.featured ? 'text-4xl' : 'text-3xl'}`}
                        ></i>
                      </div>
                    </button>
                  ) : (
                    <div className="absolute inset-0 bg-gradient-to-br from-amber-900/80 to-orange-900/80 flex items-center justify-center">
                      <div className="text-center">
                        <i className="ri-time-line text-4xl text-white mb-2 block"></i>
                        <p className="text-white font-bold text-lg">Coming Soon</p>
                      </div>
                    </div>
                  )}

                  {/* Episode badge */}
                  <div className="absolute top-3 left-3 px-3 py-1 bg-amber-500 text-white text-xs font-bold rounded-full">
                    Episode {post.episode}
                  </div>

                  {/* Featured badge */}
                  {post.featured && (
                    <div className="absolute top-3 right-3 flex items-center gap-1 px-3 py-1 bg-[#1C2B1E] text-white text-xs font-bold rounded-full">
                      <i className="ri-star-fill"></i>
                      Featured
                    </div>
                  )}

                  {/* Duration */}
                  <div className="absolute bottom-3 right-3 px-2 py-1 bg-black/80 text-white text-xs font-semibold rounded">
                    {post.duration}
                  </div>

                  {/* TikTok badge */}
                  {post.tiktokClipUrl && (
                    <a
                      href={post.tiktokClipUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="absolute bottom-3 left-3 px-3 py-1 bg-gradient-to-r from-pink-500 to-purple-600 text-white text-xs font-bold rounded-full hover:from-pink-600 hover:to-purple-700 transition-all flex items-center gap-1"
                    >
                      <i className="ri-tiktok-fill"></i>TikTok
                    </a>
                  )}
                </div>

                {/* Content */}
                <div className="p-5 flex flex-col flex-1">
                  <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
                    <i className="ri-calendar-line"></i>
                    <span>{post.publishDate}</span>
                    <span className="mx-1">·</span>
                    <i className="ri-eye-line"></i>
                    <span>{post.views.toLocaleString()} views</span>
                  </div>

                  <h3
                    className={`font-bold text-gray-900 mb-2 group-hover:text-amber-600 transition-colors ${
                      post.featured ? 'text-xl line-clamp-3' : 'text-lg line-clamp-2'
                    }`}
                  >
                    {post.title}
                  </h3>

                  <p
                    className={`text-sm text-gray-600 mb-4 flex-1 ${
                      post.featured ? 'line-clamp-4' : 'line-clamp-2'
                    }`}
                  >
                    {post.description}
                  </p>

                  <div className="flex flex-wrap gap-2 mt-auto">
                    {post.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* YouTube modal */}
      {selectedVideo && (
        <div
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedVideo(null)}
        >
          <button
            onClick={() => setSelectedVideo(null)}
            className="absolute top-4 right-4 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-all cursor-pointer"
            aria-label="Close video"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
          <div
            className="w-full max-w-5xl aspect-video"
            onClick={(e) => e.stopPropagation()}
          >
            <iframe
              src={`https://www.youtube.com/embed/${selectedVideo}?autoplay=1`}
              title="YouTube video player"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="w-full h-full rounded-lg"
            ></iframe>
          </div>
        </div>
      )}
    </div>
  );
}
